import { useParams, Link } from 'react-router-dom';
import { articleList } from '@/mocks/articleData';
import { softwareList } from '@/mocks/softwareData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articleList.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300 mb-4">
            <i className="ri-article-line text-2xl"></i>
          </div>
          <p className="text-foreground-600 text-lg font-medium mb-2">文章未找到</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
        </div>
      </main>
    );
  }

  const relatedArticles = articleList.filter((a) => article.relatedIds.includes(a.id));

  return (
    <main className="pt-16">
      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400">下载资讯</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-10 pb-8">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span className="px-3 py-1 text-xs font-medium bg-accent-50 text-accent-700 rounded-full">{article.category}</span>
          {article.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-500 rounded-full">{tag}</span>
          ))}
        </div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight mb-4">{article.title}</h1>
        <div className="flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
          <span><i className="ri-user-line mr-1"></i>{article.author}</span>
          <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
          <span><i className="ri-eye-line mr-1"></i>{article.readCount} 阅读</span>
        </div>
      </div>

      {/* Cover Image */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
        <div className="aspect-[16/9] rounded-2xl overflow-hidden">
          <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 md:px-6 pb-12">
        <div className="prose-custom text-foreground-800 leading-relaxed text-[15px] space-y-4">
          {article.content.split('\n\n').map((paragraph, idx) => {
            if (paragraph.startsWith('## ')) {
              return (
                <h2 key={idx} className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">
                  {paragraph.replace('## ', '')}
                </h2>
              );
            }
            if (paragraph.startsWith('- **')) {
              const items = paragraph.split('\n').filter(Boolean);
              return (
                <ul key={idx} className="space-y-2 pl-4">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground-700">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      <span dangerouslySetInnerHTML={{ __html: item.replace(/^- /, '').replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-foreground-900">$1</strong>') }}></span>
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={idx} className="text-foreground-700 leading-relaxed">
                {paragraph}
              </p>
            );
          })}
        </div>

        {/* Tags */}
        <div className="mt-10 pt-8 border-t border-background-200/70">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full hover:bg-background-200 cursor-pointer transition-colors">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="px-2 py-0.5 text-[11px] font-medium bg-accent-50 text-accent-700 rounded-md">{ra.category}</span>
                    <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 mt-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{ra.publishDate} · {ra.readCount} 阅读</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishDate,
            publisher: { '@type': 'Organization', name: '91下载', url: 'https://www.91xiazai.com/' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.91xiazai.com/article/${article.id}` },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91xiazai.com/' },
              { '@type': 'ListItem', position: 2, name: '下载资讯', item: 'https://www.91xiazai.com/' },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.91xiazai.com/article/${article.id}` },
            ],
          }),
        }}
      />
    </main>
  );
}